/**
 * Owns the Practice-mode tick loop.
 *
 * On {@link start}, declares + starts a {@link ManeuverStore} session and
 * spins up a 100 ms interval (outside Angular's zone) that builds a
 * {@link TickInput} from the live sim + ambient stores and feeds the
 * state machine. When the SM reaches a terminal phase, the runner
 * persists the run to {@link PracticeStore} and surfaces the breakdown
 * via {@link lastResult} for the result modal.
 *
 * Notes:
 *   - Target is derived from `def.targetKind` + the live sim state at
 *     declare time (see {@link deriveTarget}). MVP-playable maneuvers:
 *     `weighing.anchor` (current-anchor) and `anchoring.standard`
 *     (anchor-zone pinned to the boat at declare).
 *   - `weighing.anchor` will refuse to start with
 *     {@link PracticeStartError} `"anchor_not_deployed"` when the
 *     anchor is still up — otherwise `anchorState: "up"` would be met
 *     immediately and the SM would complete in one tick.
 *   - Impact events are consumed by `SceneCanvasComponent` (for audio),
 *     so the runner can't pull them off the sim queue directly. Instead
 *     it watches {@link ImpactScoreStore.lastImpact} and emits a single
 *     synthetic event per tick when a new impact is observed. The SM
 *     only uses the peak speed + a "hadCollision" flag — both are
 *     idempotent under duplicate ingestion.
 *   - Over-tensioned cleat detection isn't surfaced yet; passed as `[]`.
 */

import { Injectable, NgZone, computed, inject, signal } from "@angular/core";

import type { BoatImpactEvent } from "../../simulation-core";
import { BoatTelemetryStore } from "../core/sim/boat-telemetry.store";
import { ImpactScoreStore } from "../core/sim/impact-score.store";
import { SimService } from "../core/sim/sim.service";
import { GamificationConfigService } from "../core/gamification/config.service";
import type { TargetGeometry } from "../core/gamification/maneuver-context";
import {
  ManeuverStore,
  type ManeuverDeclaration,
  type ManeuverResult,
} from "../core/gamification/maneuver.store";
import { PracticeStore } from "../core/gamification/practice.store";
import type { PracticeSessionEntry } from "../core/gamification/practice-repo";
import type { PracticeLaunch } from "./practice-setup.component";
import type {
  ScoringConfig,
  ManeuverDef,
} from "../core/gamification/gamification.types";
import type { AppState } from "../../simulation-core";
import type { BoatTelemetry } from "../../simulation-core/sim/BoatTelemetry";

const TICK_MS = 100;

/**
 * Error codes the runner surfaces from {@link PracticeRunnerService.start}.
 * Callers (AppComponent) translate these into user-facing toasts.
 */
export type PracticeStartErrorCode =
  | "unknown_maneuver"
  | "anchor_not_deployed"
  | "sim_not_ready";

export class PracticeStartError extends Error {
  constructor(
    public readonly code: PracticeStartErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "PracticeStartError";
  }
}

export interface PracticeRunResult {
  readonly declaration: ManeuverDeclaration;
  readonly maneuverResult: ManeuverResult;
  readonly entry: PracticeSessionEntry;
}

@Injectable({ providedIn: "root" })
export class PracticeRunnerService {
  private readonly sim = inject(SimService);
  private readonly impact = inject(ImpactScoreStore);
  private readonly telemetry = inject(BoatTelemetryStore);
  private readonly store = inject(ManeuverStore);
  private readonly practice = inject(PracticeStore);
  private readonly gamification = inject(GamificationConfigService);
  private readonly zone = inject(NgZone);

  /** Most recent finished run (success / failure / abort) waiting for dismissal. */
  readonly lastResult = signal<PracticeRunResult | null>(null);

  /** True between start() and the terminal phase. */
  readonly isRunning = computed(() => {
    const p = this.store.phase();
    return p === "declared" || p === "in_progress" || p === "stabilizing";
  });

  /** Forward the live ManeuverStore signals so the HUD can bind to them. */
  readonly phase = this.store.phase;
  readonly elapsedSec = this.store.elapsedSec;
  readonly checklist = this.store.checklist;
  readonly declaration = this.store.declaration;

  private timer: ReturnType<typeof setInterval> | null = null;
  private lastTickMs = 0;
  private lastImpactTms = 0;
  private scoring: ScoringConfig | null = null;

  /**
   * Begin a Practice session for the given launch payload.
   *
   * Throws {@link PracticeStartError} for predictable failures (no such
   * maneuver / wrong sim state). The tick loop continues asynchronously
   * after the SM is declared and started.
   */
  async start(launch: PracticeLaunch): Promise<void> {
    // Guard against a stale timer if start() is called twice (e.g. user
    // hits Start twice on a slow await).
    this.stopTimer();
    const cfg = await this.gamification.load();
    const def = cfg.maneuvers.maneuvers.find((m) => m.id === launch.maneuverId);
    if (!def) {
      throw new PracticeStartError(
        "unknown_maneuver",
        `PracticeRunner: unknown maneuver "${launch.maneuverId}"`,
      );
    }
    const state = this.sim.liveState();
    const tele = this.telemetry.state();
    if (!state || !tele) {
      throw new PracticeStartError(
        "sim_not_ready",
        "PracticeRunner: sim not yet attached",
      );
    }
    // `weighing.anchor` requires the anchor to actually be deployed at
    // the seabed — otherwise `anchorState: "up"` is already met and the
    // SM would complete in one tick.
    if (def.id === "weighing.anchor" && state.anchorWorldX === null) {
      throw new PracticeStartError(
        "anchor_not_deployed",
        "PracticeRunner: weigh-anchor requires a deployed anchor",
      );
    }
    this.scoring = cfg.scoring;

    this.stopTimer();
    this.store.reset();
    this.lastResult.set(null);

    const declaration: ManeuverDeclaration = {
      def,
      tier: launch.tier,
      boatId: launch.boatId,
      terrainId: launch.terrainId,
      target: deriveTarget(def, state, tele),
      isFirstAttempt: !this.practice
        .history()
        .some(
          (e) => e.maneuverId === launch.maneuverId && e.status === "completed",
        ),
    };
    this.store.declare(declaration);

    // Spawn any course-marker buoys declared by the maneuver. Offsets
    // are in the boat's body frame at declare time so the spec is
    // map-agnostic. Idempotent on retry (spawnBuoy dedupes by id).
    if (def.buoySpawns && def.buoySpawns.length > 0) {
      const ch = Math.cos(tele.heading);
      const sh = Math.sin(tele.heading);
      for (const sp of def.buoySpawns) {
        // Boat forward is +Z_body (Z in app frame); right is +X_body.
        // World X/Z follow the existing heading convention: bow at
        // (sin h, -cos h). See main.ts / BoatHud for parity.
        const wx = tele.posX + sp.forwardM * sh + sp.rightM * ch;
        const wz = tele.posZ + sp.forwardM * -ch + sp.rightM * sh;
        this.sim.spawnBuoy(sp.kind, wx, wz, {
          id: `practice.${def.id}.${sp.id}`,
          anchored: sp.anchored,
        });
      }
    }

    const now = performance.now();
    this.store.start(now);
    this.lastTickMs = now;
    this.lastImpactTms = this.impact.lastImpact()?.tMs ?? 0;

    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => this.tick(), TICK_MS);
    });
  }

  /** Cancel the in-flight maneuver. Records an aborted entry. */
  abort(): void {
    if (!this.isRunning() && this.store.phase() === "idle") return;
    this.stopTimer();
    this.store.abort();
    this.finalize();
  }

  /** Acknowledge the result modal — clears `lastResult` and the SM. */
  dismissResult(): void {
    this.lastResult.set(null);
    this.store.reset();
  }

  /** Convenience for the maneuver definition (HUD label / target time). */
  currentDef(): ManeuverDef | null {
    return this.declaration()?.def ?? null;
  }

  private tick(): void {
    const scoring = this.scoring;
    if (!scoring) return;
    const state = this.sim.liveState();
    const tele = this.telemetry.state();
    if (!state || !tele) return;

    const now = performance.now();
    const dt = Math.max(0, (now - this.lastTickMs) / 1000);
    this.lastTickMs = now;

    const events = this.drainImpactEvent();

    this.store.tick({
      nowMs: now,
      dtSec: dt,
      state,
      telemetry: tele,
      impactEvents: events,
      overTensionedCleats: [],
      scoring,
    });

    const phase = this.store.phase();
    if (phase === "completed" || phase === "failed") {
      this.stopTimer();
      this.finalize();
    }
  }

  /**
   * Build the per-tick impact-event list from {@link ImpactScoreStore.lastImpact}.
   * Returns at most one event per tick (the most recent), and only if it
   * post-dates the previously observed timestamp.
   */
  private drainImpactEvent(): BoatImpactEvent[] {
    const li = this.impact.lastImpact();
    if (!li) return [];
    if (li.tMs <= this.lastImpactTms) return [];
    this.lastImpactTms = li.tMs;
    return [{ kind: li.kind, speed: li.speed }];
  }

  private finalize(): void {
    const result = this.store.result();
    const decl = this.store.declaration();
    if (!result || !decl) return;
    const entry = this.practice.record(decl, result);
    this.lastResult.set({
      declaration: decl,
      maneuverResult: result,
      entry,
    });
  }

  private stopTimer(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

/**
 * Build the maneuver target from `def.targetKind` + the live sim state.
 *
 * MVP targets:
 *   - `current-anchor` (weighing.anchor): the seabed anchor position,
 *     known to be non-null because we gated on it in {@link start}.
 *   - `anchor-zone` (anchoring.standard): the player's drop point — we
 *     pin this to the boat's current position at declare time so that
 *     distance-based requirements (none today, but the field is read
 *     by the context builder for telemetry) have a sensible reference.
 *   - everything else: `null`. The remaining playable maneuvers don't
 *     exist yet (filtered out at the picker level).
 */
function deriveTarget(
  def: ManeuverDef,
  state: AppState,
  tele: BoatTelemetry,
): TargetGeometry | null {
  switch (def.targetKind) {
    case "current-anchor":
      if (state.anchorWorldX !== null && state.anchorWorldZ !== null) {
        return { worldX: state.anchorWorldX, worldZ: state.anchorWorldZ };
      }
      return null;
    case "anchor-zone":
      return { worldX: tele.posX, worldZ: tele.posZ };
    default:
      return null;
  }
}
